// 1.2.18: 查询历史服务 - 添加前端缓存优化
import { supabase, getAnonymousUserId, getUserId } from '../lib/supabase';
import type { QueryHistoryItem, QueryMetadata } from '../../features/history/types/queryHistory';
import type { Restaurant } from '../../features/search/types/restaurant';
import type { ChatMessage } from '../../features/chat/types/chat';
import { getCurrentUser } from './authService';
import { logger } from '../utils/logger';

// v1.3.0: Get Supabase URL and project ref for Edge Function URL
// 优先使用 VITE_SUPABASE_URL（支持本地开发），如果没有则使用 PROJECT_REF
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PROJECT_REF = import.meta.env.VITE_SUPABASE_PROJECT_REF;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const ARCHIVE_SERVICE_URL = SUPABASE_URL
  ? `${SUPABASE_URL}/functions/v1/archive-query-history`
  : SUPABASE_PROJECT_REF && SUPABASE_PROJECT_REF !== 'local'
    ? `https://${SUPABASE_PROJECT_REF}.supabase.co/functions/v1/archive-query-history`
    : null;

// Snapshot expiration: 7 days in milliseconds
const SNAPSHOT_EXPIRATION_MS = 7 * 24 * 60 * 60 * 1000;

// 1.2.18: 查询历史缓存相关常量
const QUERY_HISTORY_CACHE_KEY_PREFIX = 'query_history_cache_';
const CACHE_TTL = 30 * 24 * 60 * 60 * 1000; // 1个月，30天
const CACHE_VERSION = '1.2.18'; // 缓存版本，用于版本兼容性

// 1.2.18: 缓存数据结构
interface QueryHistoryCache {
  userId: string;
  history: QueryHistoryItem[];
  timestamp: number;
  version: string;
}

// ==================== Query History Cache Management (localStorage) ====================

/**
 * 1.2.18: 获取缓存键名
 */
function getQueryHistoryCacheKey(userId: string): string {
  return `${QUERY_HISTORY_CACHE_KEY_PREFIX}${userId}`;
}

/**
 * 1.2.18: 获取查询历史缓存
 */
function getQueryHistoryCache(userId: string): QueryHistoryCache | null {
  try {
    const cacheKey = getQueryHistoryCacheKey(userId);
    const data = localStorage.getItem(cacheKey);
    if (!data) {
      return null;
    }
    return JSON.parse(data) as QueryHistoryCache;
  } catch (error: any) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error getting query history cache:', error);
    }
    // 缓存解析失败，清除无效缓存
    clearQueryHistoryCache(userId);
    return null;
  }
}

/**
 * 1.2.18: 设置查询历史缓存
 */
function setQueryHistoryCache(userId: string, history: QueryHistoryItem[]): void {
  try {
    const cacheKey = getQueryHistoryCacheKey(userId);
    const cache: QueryHistoryCache = {
      userId,
      history,
      timestamp: Date.now(),
      version: CACHE_VERSION,
    };
    localStorage.setItem(cacheKey, JSON.stringify(cache));
    if (import.meta.env.DEV) {
      logger.log(`✅ Cached ${history.length} query history items for user ${userId}`);
    }
  } catch (error: any) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error setting query history cache:', error);
    }
  }
}

/**
 * 1.2.18: 清除查询历史缓存（导出供外部使用）
 */
export function clearQueryHistoryCache(userId: string): void {
  try {
    const cacheKey = getQueryHistoryCacheKey(userId);
    localStorage.removeItem(cacheKey);
    if (import.meta.env.DEV) {
      logger.log(`✅ Cleared query history cache for user ${userId}`);
    }
  } catch (error: any) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error clearing query history cache:', error);
    }
  }
}

/**
 * 1.2.18: 检查是否有有效的查询历史缓存（导出供外部使用）
 */
export function hasValidQueryHistoryCache(userId: string): boolean {
  const cache = getQueryHistoryCache(userId);
  return isQueryHistoryCacheValid(cache, userId);
}

/**
 * 1.2.18: 获取缓存的查询历史（导出供外部使用，用于立即显示）
 */
export function getCachedQueryHistory(userId: string): QueryHistoryItem[] {
  const cache = getQueryHistoryCache(userId);
  if (isQueryHistoryCacheValid(cache, userId)) {
    return cache!.history;
  }
  return [];
}

/**
 * 1.2.18: 验证查询历史缓存有效性
 */
function isQueryHistoryCacheValid(cache: QueryHistoryCache | null, userId: string): boolean {
  if (!cache) {
    return false;
  }
  
  // 检查用户ID是否匹配
  if (cache.userId !== userId) {
    return false;
  }
  
  // 检查版本是否匹配（版本不匹配时清除旧缓存）
  if (cache.version !== CACHE_VERSION) {
    if (import.meta.env.DEV) {
      logger.log(`⚠️ Query history cache version mismatch (${cache.version} vs ${CACHE_VERSION}), clearing cache`);
    }
    clearQueryHistoryCache(userId);
    return false;
  }
  
  // 检查缓存是否过期
  const now = Date.now();
  const age = now - cache.timestamp;
  if (age > CACHE_TTL) {
    if (import.meta.env.DEV) {
      logger.log(`⚠️ Query history cache expired (age: ${Math.round(age / 1000 / 60)} minutes), clearing cache`);
    }
    clearQueryHistoryCache(userId);
    return false;
  }
  
  return true;
}

/**
 * Calculate expiration date for snapshot (7 days from now)
 */
function getSnapshotExpirationDate(): string {
  const expirationDate = new Date(Date.now() + SNAPSHOT_EXPIRATION_MS);
  return expirationDate.toISOString();
}

/**
 * Check if a snapshot is still valid (not expired)
 */
export function isSnapshotValid(item: QueryHistoryItem): boolean {
  if (!item.results_snapshot || !item.snapshot_expires_at) {
    return false;
  }
  
  const expirationDate = new Date(item.snapshot_expires_at);
  const now = new Date();
  return now < expirationDate;
}

/**
 * Get user query count (queries with snapshots or conversation messages)
 * Used to enforce the 5-query limit for anonymous users and display saved count
 */
export async function getUserQueryCount(): Promise<number> {
  try {
    const { userId, isAuthenticated } = await getUserId();
    const user = await getCurrentUser();
    
    if (!userId) {
      return 0;
    }

    // For authenticated users: query by auth_user_id
    // For anonymous users: query by user_id
    // Count queries that have either results_snapshot OR conversation_messages
    // Using PostgREST OR syntax: (field1.not.is.null,field2.not.is.null)
    let query;
    if (isAuthenticated && user) {
      query = supabase
        .from('query_history')
        .select('id', { count: 'exact', head: true })
        .or('results_snapshot.not.is.null,conversation_messages.not.is.null')
        .eq('auth_user_id', user.id);
    } else {
      query = supabase
        .from('query_history')
        .select('id', { count: 'exact', head: true })
        .or('results_snapshot.not.is.null,conversation_messages.not.is.null')
        .eq('user_id', userId);
    }

    const { count, error } = await query;

    if (error) {
      logger.error('❌ Error getting user query count:', error);
      return 0;
    }

    return count || 0;
  } catch (error: any) {
    logger.error('❌ Exception while getting user query count:', error);
    return 0;
  }
}

/**
 * Check if user can save more queries (enforces 5-query limit for anonymous users)
 */
export async function checkQueryLimit(): Promise<{ allowed: boolean; count: number; limit: number }> {
  try {
    const { isAuthenticated } = await getUserId();
    
    if (isAuthenticated) {
      // Authenticated users have no limit
      return {
        allowed: true,
        count: 0,
        limit: Infinity,
      };
    }

    // Anonymous users have a limit of 5 queries with snapshots
    const count = await getUserQueryCount();
    const limit = 5;

    return {
      allowed: count < limit,
      count,
      limit,
    };
  } catch (error: any) {
    logger.error('❌ Exception while checking query limit:', error);
    // On error, allow saving (fail open)
    return {
      allowed: true,
      count: 0,
      limit: 5,
    };
  }
}

/**
 * Save a query to history
 */
export async function saveQuery(
  queryText: string,
  resultCount: number | null = null,
  metadata: QueryMetadata | null = null,
  resultsSnapshot?: Restaurant[] | null,
  conversationMessages?: ChatMessage[] | null
): Promise<{ item: QueryHistoryItem | null; error: string | null }> {
  try {
    const { userId, isAuthenticated } = await getUserId();
    const user = await getCurrentUser();
    
    if (!userId) {
      logger.error('❌ Cannot save query: user_id is missing');
      return { item: null, error: '无法保存查询：用户ID缺失' };
    }

    // Check query limit for anonymous users
    // The limit check should trigger if:
    // 1. We have restaurants to save (resultsSnapshot) - this will create a query with snapshot
    // 2. OR we have conversation messages to save (conversationMessages) - this will create a conversation
    // 3. OR we're saving a query (even without snapshot) - in search mode, query is saved first, then snapshot is added later
    // For search mode: query is saved first without snapshot, then updateQueryResultCount adds snapshot
    // We need to check limit when saving query (even without snapshot) to prevent saving queries that will exceed limit
    const hasDataToSave = (resultsSnapshot && resultsSnapshot.length > 0) || 
                          (conversationMessages && conversationMessages.length > 0);
    
    // For anonymous users, check limit if:
    // 1. We have data to save (snapshot or conversation) - immediate limit check
    // 2. OR we're saving a query (even without snapshot) - check if adding snapshot would exceed limit
    // This ensures that in search mode, we check limit before saving the query
    if (!isAuthenticated) {
      const limitCheck = await checkQueryLimit();
      logger.log('🔍 Checking query limit:', {
        hasDataToSave,
        isAuthenticated,
        limitCheck,
        hasSnapshot: !!(resultsSnapshot && resultsSnapshot.length > 0),
        hasConversation: !!(conversationMessages && conversationMessages.length > 0),
        queryText: queryText.substring(0, 50) + '...'
      });
      
      // If we have data to save (snapshot or conversation), check limit immediately
      // If we don't have data yet (search mode - query saved first, snapshot added later),
      // check if adding this query would exceed limit (count >= limit means no more allowed)
      if (hasDataToSave && !limitCheck.allowed) {
        logger.warn('⚠️ Query limit reached for anonymous user (with data):', limitCheck);
        const errorMessage = `未注册用户最多只能保存 ${limitCheck.limit} 个查询结果。请注册以保存更多查询。`;
        logger.error('❌ Query limit error:', errorMessage);
        return {
          item: null,
          error: errorMessage,
        };
      } else if (!hasDataToSave && limitCheck.count >= limitCheck.limit) {
        // In search mode: query is saved first without snapshot, then snapshot is added
        // If we're already at the limit, don't allow saving more queries (even without snapshot)
        // because the snapshot will be added later via updateQueryResultCount
        logger.warn('⚠️ Query limit reached for anonymous user (no data yet, but at limit):', limitCheck);
        const errorMessage = `未注册用户最多只能保存 ${limitCheck.limit} 个查询结果。请注册以保存更多查询。`;
        logger.error('❌ Query limit error:', errorMessage);
        return {
          item: null,
          error: errorMessage,
        };
      }
    }

    logger.log('💾 Attempting to save query:', {
      queryText,
      userId,
      isAuthenticated,
      resultCount,
      hasSnapshot: !!resultsSnapshot,
      snapshotCount: resultsSnapshot?.length || 0,
      hasConversation: !!conversationMessages,
      conversationCount: conversationMessages?.length || 0
    });

    const insertData: any = {
      query_text: queryText,
      result_count: resultCount,
      metadata: metadata,
    };

    // For authenticated users: use auth_user_id (from auth.users.id) and keep user_id (anonymous ID if exists)
    // For anonymous users: use user_id (anonymous ID) only
    if (isAuthenticated && user) {
      // Authenticated user: set auth_user_id to user.id from auth.users
      insertData.auth_user_id = user.id;
      // Keep user_id as anonymous ID if it exists (for continuity)
      // Get anonymous ID from localStorage if available
      const anonymousUserId = getAnonymousUserId();
      insertData.user_id = anonymousUserId;
    } else {
      // Anonymous user: only set user_id
      insertData.user_id = userId;
      insertData.auth_user_id = null;
    }

    // If results are provided, save snapshot with expiration
    if (resultsSnapshot && resultsSnapshot.length > 0) {
      insertData.results_snapshot = resultsSnapshot;
      insertData.snapshot_expires_at = getSnapshotExpirationDate();
    }
    
    // If conversation messages are provided, save conversation history
    if (conversationMessages && conversationMessages.length > 0) {
      // Ensure conversation messages are serializable
      // Remove any circular references or non-serializable data
      try {
        // Test serialization to catch any issues early
        JSON.stringify(conversationMessages);
        insertData.conversation_messages = conversationMessages;
      } catch (serializeError) {
        logger.warn('⚠️ Conversation messages contain non-serializable data, saving without conversation history:', serializeError);
        // Don't include conversation_messages if it can't be serialized
        // The query will still be saved, just without conversation history
      }
    }

    const { data, error } = await supabase
      .from('query_history')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      // Serialize error object properly for logging
      try {
        const errorDetails = {
          code: error.code || 'UNKNOWN',
          message: error.message || 'Unknown error',
          details: error.details || null,
          hint: error.hint || null,
          insertData: {
            query_text: insertData.query_text,
            user_id: insertData.user_id,
            auth_user_id: insertData.auth_user_id,
            result_count: insertData.result_count,
            hasMetadata: !!insertData.metadata,
            hasSnapshot: !!insertData.results_snapshot,
            snapshotCount: insertData.results_snapshot?.length || 0,
            hasConversation: !!insertData.conversation_messages,
            conversationCount: insertData.conversation_messages?.length || 0
          }
        };
        
        logger.error('❌ Error saving query to database:', JSON.stringify(errorDetails, null, 2));
      } catch (serializeError) {
        // If serialization fails, log basic error info
        logger.error('❌ Error saving query to database (serialization failed):', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
      }
      
      // Always log the full error object for debugging
      logger.error('❌ Full error object:', error);

      // Check for common error types and handle them appropriately
      
      // Error code PGRST204: Column not found (missing migration)
      // Error code 42501: Permission denied (RLS policy issue)
      // Error code 23502: Not null violation
      // Error code 23503: Foreign key violation
      
      if (error.code === 'PGRST204' || error.message?.includes('column') || error.message?.includes('does not exist')) {
        // Missing column - try without optional fields
        const missingColumn = error.message?.match(/Could not find the '(\w+)' column/)?.[1] || 'unknown';
        logger.warn(`⚠️ Column '${missingColumn}' not found, saving without optional fields. Please run migration.`);
        logger.warn('📝 Migration file: backend/supabase/migrations/20250112000000_add_conversation_messages.sql');
        
        const insertDataMinimal: any = {
          query_text: queryText,
          result_count: resultCount,
          metadata: metadata,
        };
        
        // Apply same user ID logic as above
        if (isAuthenticated && user) {
          insertDataMinimal.auth_user_id = user.id;
          const anonymousUserId = getAnonymousUserId();
          insertDataMinimal.user_id = anonymousUserId;
        } else {
          insertDataMinimal.user_id = userId;
          insertDataMinimal.auth_user_id = null;
        }
        
        // Only include snapshot if the error wasn't about snapshot columns
        if (missingColumn !== 'results_snapshot' && missingColumn !== 'snapshot_expires_at') {
          if (resultsSnapshot && resultsSnapshot.length > 0) {
            insertDataMinimal.results_snapshot = resultsSnapshot;
            insertDataMinimal.snapshot_expires_at = getSnapshotExpirationDate();
          }
        }
        
        // Don't include conversation_messages if that's the missing column
        // (or if we're retrying after any column error, to be safe)
        
        logger.log('🔄 Retrying save without optional fields:', {
          hasSnapshot: !!insertDataMinimal.results_snapshot,
          hasConversation: false, // Always exclude conversation_messages on retry
          hasMetadata: !!insertDataMinimal.metadata
        });
        
        const { data: retryData, error: retryError } = await supabase
          .from('query_history')
          .insert(insertDataMinimal)
          .select()
          .single();
        
        if (retryError) {
          logger.error('❌ Error saving query (retry without optional fields):', JSON.stringify({
            code: retryError.code,
            message: retryError.message,
            details: retryError.details,
            hint: retryError.hint
          }, null, 2));
          return { item: null, error: retryError.message || '保存查询失败' };
        }
        
        logger.log('✅ Query saved successfully (without optional fields)');
        logger.warn('⚠️ Note: Conversation history was not saved. Please run migration to enable full functionality.');
        return { item: retryData as QueryHistoryItem, error: null };
      }
      
      // RLS policy error - provide helpful message
      if (error.code === '42501' || error.message?.includes('permission') || error.message?.includes('policy')) {
        logger.error('❌ RLS Policy Error: Check if user has proper permissions');
        return { item: null, error: '权限错误：无法保存查询。请检查用户权限设置。' };
      }
      
      // Return a more descriptive error message
      const errorMessage = error.message || error.details || error.hint || '保存查询失败';
      return { item: null, error: errorMessage };
    }

    logger.log('✅ Query saved successfully:', {
      id: data?.id,
      query_text: data?.query_text,
      created_at: data?.created_at,
      hasConversation: !!data?.conversation_messages
    });

    return { item: data as QueryHistoryItem, error: null };
  } catch (error: any) {
    // Properly serialize error for logging
    const errorInfo = {
      message: error?.message || 'Unknown error',
      stack: error?.stack || null,
      name: error?.name || null,
      code: error?.code || null,
      details: error?.details || null
    };
    logger.error('❌ Exception while saving query:', JSON.stringify(errorInfo, null, 2));
    logger.error('❌ Full error object:', error);
    return { item: null, error: error?.message || '保存查询失败' };
  }
}

/**
 * @deprecated 1.5.16: Phase 4 - 使用 conversationService.updateConversation 代替
 * Update existing conversation in query history
 * This updates an existing conversation thread instead of creating a new one
 */
export async function updateConversation(
  conversationId: string,
  conversationMessages: ChatMessage[],
  metadata?: QueryMetadata
): Promise<{ item: QueryHistoryItem | null; error: string | null }> {
  try {
    if (!conversationMessages || conversationMessages.length === 0) {
      logger.warn('⚠️ No conversation messages to update');
      return { item: null, error: '没有对话消息需要更新' };
    }

    const { userId, isAuthenticated } = await getUserId();
    const user = await getCurrentUser();
    
    if (!userId) {
      logger.error('❌ Cannot update conversation: user_id is missing');
      return { item: null, error: '无法更新对话：用户ID缺失' };
    }

    // Extract all restaurants from conversation messages
    const allRestaurants: Restaurant[] = [];
    conversationMessages.forEach(msg => {
      if (msg.restaurants && msg.restaurants.length > 0) {
        msg.restaurants.forEach(r => {
          if (!allRestaurants.find(existing => existing.id === r.id)) {
            allRestaurants.push(r);
          }
        });
      }
    });

    const updateData: any = {
      conversation_messages: conversationMessages,
      result_count: allRestaurants.length > 0 ? allRestaurants.length : null,
    };

    // Update metadata if provided
    if (metadata) {
      updateData.metadata = {
        ...metadata,
        source: 'chat',
        language: metadata.language || 'zh',
      };
    }

    // Update snapshot if restaurants are available
    if (allRestaurants.length > 0) {
      updateData.results_snapshot = allRestaurants;
      updateData.snapshot_expires_at = getSnapshotExpirationDate();
    }

    // For authenticated users: check by auth_user_id
    // For anonymous users: check by user_id
    let query;
    if (isAuthenticated && user) {
      query = supabase
        .from('query_history')
        .update(updateData)
        .eq('id', conversationId)
        .eq('auth_user_id', user.id)
        .select()
        .single();
    } else {
      query = supabase
        .from('query_history')
        .update(updateData)
        .eq('id', conversationId)
        .eq('user_id', userId)
        .select()
        .single();
    }

    const { data, error } = await query;

    if (error) {
      logger.error('❌ Error updating conversation:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
        conversationId
      });
      return { item: null, error: error.message || '更新对话历史失败' };
    }

    if (data) {
      logger.log('✅ Conversation updated successfully:', {
        id: data.id,
        messageCount: conversationMessages.length,
        restaurantCount: allRestaurants.length
      });
    }

    return { item: data as QueryHistoryItem, error: null };
  } catch (error: any) {
    logger.error('❌ Exception while updating conversation:', {
      error: error.message,
      stack: error.stack
    });
    return { item: null, error: error.message || '更新对话历史失败' };
  }
}

/**
 * @deprecated 1.5.16: Phase 4 - 使用 conversationService.saveConversation 代替
 * Save conversation history to query history
 * This is specifically for chat mode where we want to save the full conversation
 * If conversationId is provided, updates existing conversation; otherwise creates new one
 */
export async function saveConversation(
  conversationMessages: ChatMessage[],
  metadata?: QueryMetadata,
  conversationId?: string | null
): Promise<{ item: QueryHistoryItem | null; error: string | null }> {
  try {
    if (!conversationMessages || conversationMessages.length === 0) {
      logger.warn('⚠️ No conversation messages to save');
      return { item: null, error: '没有对话消息需要保存' };
    }

    // If conversationId is provided, update existing conversation
    if (conversationId) {
      logger.log('🔄 Updating existing conversation:', conversationId);
      return await updateConversation(conversationId, conversationMessages, metadata);
    }

    // Otherwise, create new conversation
    logger.log('💾 Creating new conversation');
    
    // Extract user's first message as query_text
    const firstUserMessage = conversationMessages.find(msg => msg.role === 'user');
    const queryText = firstUserMessage?.content || 'Chat conversation';
    
    // Extract all restaurants from conversation messages
    const allRestaurants: Restaurant[] = [];
    conversationMessages.forEach(msg => {
      if (msg.restaurants && msg.restaurants.length > 0) {
        msg.restaurants.forEach(r => {
          if (!allRestaurants.find(existing => existing.id === r.id)) {
            allRestaurants.push(r);
          }
        });
      }
    });
    
    // Prepare metadata
    const conversationMetadata: QueryMetadata = {
      ...metadata,
      source: 'chat',
      language: metadata?.language || 'zh',
    };
    
    // Save conversation to query history
    const result = await saveQuery(
      queryText,
      allRestaurants.length > 0 ? allRestaurants.length : null,
      conversationMetadata,
      allRestaurants.length > 0 ? allRestaurants : null,
      conversationMessages
    );
    
    if (result.item) {
      logger.log('✅ Conversation saved successfully:', {
        id: result.item.id,
        messageCount: conversationMessages.length,
        restaurantCount: allRestaurants.length
      });
    }
    
    return result;
  } catch (error: any) {
    logger.error('❌ Exception while saving conversation:', {
      error: error.message,
      stack: error.stack
    });
    return { item: null, error: error.message || '保存对话历史失败' };
  }
}

/**
 * Get query history for the current user (lightweight version)
 * Excludes large fields (results_snapshot, conversation_messages) for better performance
 * Only returns queries that have either results_snapshot or conversation_messages (saved queries)
 */
/**
 * Get query history for the current user
 * 1.2.18: 优化 - 先返回缓存数据，后台同步最新数据
 */
export async function getQueryHistory(limit: number = 50): Promise<QueryHistoryItem[]> {
  try {
    const { userId, isAuthenticated } = await getUserId();
    const user = await getCurrentUser();

    if (!userId) {
      if (import.meta.env.DEV) {
        logger.error('❌ Cannot get query history: user_id is missing');
      }
      return [];
    }

    // 1.2.18: 使用统一的用户ID作为缓存键（优先使用 auth_user_id，否则使用 user_id）
    const cacheUserId = (isAuthenticated && user) ? user.id : userId;

    // 1.2.18: 先检查缓存
    const cache = getQueryHistoryCache(cacheUserId);
    let cachedHistory: QueryHistoryItem[] = [];
    
    if (isQueryHistoryCacheValid(cache, cacheUserId)) {
      cachedHistory = cache!.history;
      if (import.meta.env.DEV) {
        logger.log(`📦 Using cached query history (${cachedHistory.length} items)`);
      }
      
      // 1.2.18: 后台异步同步最新数据（不阻塞返回）
      syncQueryHistoryFromBackend(limit, userId, isAuthenticated, user, cacheUserId).catch((error) => {
        if (import.meta.env.DEV) {
          logger.error('❌ Error syncing query history from backend:', error);
        }
        // 后台同步失败不影响已返回的缓存数据
      });
      
      // 立即返回缓存数据
      return cachedHistory;
    }

    // 1.2.18: 缓存无效或不存在，从后端获取
    return await syncQueryHistoryFromBackend(limit, userId, isAuthenticated, user, cacheUserId);
  } catch (error) {
    if (import.meta.env.DEV) {
      logger.error('❌ Exception while fetching query history:', error);
    }
    
    // 1.2.18: 如果后端请求失败，尝试使用缓存（即使可能过期）
    try {
      const { userId, isAuthenticated } = await getUserId();
      const user = await getCurrentUser();
      const cacheUserId = (isAuthenticated && user) ? user.id : userId;
      if (cacheUserId) {
        const cache = getQueryHistoryCache(cacheUserId);
        if (cache && cache.userId === cacheUserId) {
          if (import.meta.env.DEV) {
            logger.log('⚠️ Using stale cache due to backend error');
          }
          return cache.history;
        }
      }
    } catch (cacheError) {
      // 忽略缓存错误
    }
    
    return [];
  }
}

/**
 * 1.2.18: 从后端同步查询历史数据并更新缓存
 */
async function syncQueryHistoryFromBackend(
  limit: number,
  userId: string,
  isAuthenticated: boolean,
  user: any,
  cacheUserId: string
): Promise<QueryHistoryItem[]> {
  // Only select fields needed for list display to improve performance
  // Exclude large JSON fields: results_snapshot, conversation_messages
  const fields = 'id, query_text, user_id, auth_user_id, created_at, result_count, metadata, snapshot_expires_at';

  // For authenticated users: query by auth_user_id OR user_id (to include anonymous history)
  // For anonymous users: query by user_id
  // Only return queries that have either results_snapshot OR conversation_messages (saved queries)
  let query;
  if (isAuthenticated && user) {
    // Get anonymous user ID from localStorage if available
    const anonymousUserId = getAnonymousUserId();
    
    // Query by auth_user_id OR user_id (anonymous ID) to get all history
    // This ensures we get both authenticated and anonymous history
    // Filter to only include queries with snapshot or conversation messages
    if (anonymousUserId) {
      // Use PostgREST OR syntax: field1.eq.value1,field2.eq.value2
      query = supabase
        .from('query_history')
        .select(fields)
        .or(`auth_user_id.eq.${user.id},user_id.eq.${anonymousUserId}`)
        .or('results_snapshot.not.is.null,conversation_messages.not.is.null')
        .order('created_at', { ascending: false })
        .limit(limit);
    } else {
      // Fallback: only query by auth_user_id if no anonymous ID
      query = supabase
        .from('query_history')
        .select(fields)
        .eq('auth_user_id', user.id)
        .or('results_snapshot.not.is.null,conversation_messages.not.is.null')
        .order('created_at', { ascending: false })
        .limit(limit);
    }
  } else {
    query = supabase
      .from('query_history')
      .select(fields)
      .eq('user_id', userId)
      .or('results_snapshot.not.is.null,conversation_messages.not.is.null')
      .order('created_at', { ascending: false })
      .limit(limit);
  }

  const { data, error } = await query;

  if (error) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error fetching query history:', error);
      console.error('❌ Query history error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
        userId,
        isAuthenticated,
        userEmail: user?.email
      });
    }
    throw error;
  }

  const history = (data || []) as QueryHistoryItem[];
  
  // 1.2.18: 更新缓存
  setQueryHistoryCache(cacheUserId, history);
  
  if (import.meta.env.DEV) {
    logger.log(`✅ Synced ${history.length} query history items from backend`);
    console.log('✅ Query history fetched:', {
      count: history.length,
      userId,
      isAuthenticated,
      userEmail: user?.email,
      sampleItem: history?.[0]
    });
  }
  
  return history;
}

/**
 * Get query history with full data including snapshots and conversation messages
 * Use this when you need the complete data (e.g., when loading from history)
 */
export async function getQueryHistoryWithSnapshot(id: string): Promise<QueryHistoryItem | null> {
  try {
    const { userId, isAuthenticated } = await getUserId();
    const user = await getCurrentUser();

    if (!userId) {
      logger.error('❌ Cannot get query history: user_id is missing');
      return null;
    }

    // Get full data including large fields
    let query;
    if (isAuthenticated && user) {
      query = supabase
        .from('query_history')
        .select('*')
        .eq('id', id)
        .eq('auth_user_id', user.id)
        .single();
    } else {
      query = supabase
        .from('query_history')
        .select('*')
        .eq('id', id)
        .eq('user_id', userId)
        .single();
    }

    const { data, error } = await query;

    if (error) {
      logger.error('❌ Error fetching query history with snapshot:', error);
      return null;
    }

    return data as QueryHistoryItem | null;
  } catch (error) {
    logger.error('❌ Exception while fetching query history with snapshot:', error);
    return null;
  }
}

/**
 * Delete a single query from history
 */
export async function deleteQuery(id: string): Promise<boolean> {
  try {
    const { userId, isAuthenticated } = await getUserId();
    const user = await getCurrentUser();

    if (!userId) {
      logger.error('❌ Cannot delete query: user_id is missing');
      return false;
    }

    // For authenticated users: check by auth_user_id
    // For anonymous users: check by user_id
    let query;
    if (isAuthenticated && user) {
      query = supabase
        .from('query_history')
        .delete()
        .eq('id', id)
        .eq('auth_user_id', user.id);
    } else {
      query = supabase
        .from('query_history')
        .delete()
        .eq('id', id)
        .eq('user_id', userId);
    }

    const { error } = await query;

    if (error) {
      if (import.meta.env.DEV) {
        logger.error('❌ Error deleting query:', error);
      }
      return false;
    }

    // 1.2.18: 删除成功后清除缓存
    const cacheUserId = (isAuthenticated && user) ? user.id : userId;
    if (cacheUserId) {
      clearQueryHistoryCache(cacheUserId);
      if (import.meta.env.DEV) {
        logger.log('✅ Cleared query history cache after deletion');
      }
    }

    return true;
  } catch (error) {
    logger.error('❌ Exception while deleting query:', error);
    return false;
  }
}

/**
 * Clear all query history for the current user
 * Records are archived to query_history_archive before deletion for analysis
 */
export async function clearHistory(): Promise<boolean> {
  try {
    const { userId, isAuthenticated } = await getUserId();
    const user = await getCurrentUser();

    if (!userId) {
      logger.error('❌ Cannot clear history: user_id is missing');
      return false;
    }

    // Step 1: Archive records before deletion using Edge Function
    // The Edge Function uses service role to bypass RLS and archive records
    if (ARCHIVE_SERVICE_URL && SUPABASE_ANON_KEY) {
      try {
        logger.log('📦 Attempting to archive records via Edge Function...');
        const archiveResponse = await fetch(ARCHIVE_SERVICE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            userId: userId,
            authUserId: user?.id || null,
            isAuthenticated: isAuthenticated,
          }),
        });

        if (!archiveResponse.ok) {
          const errorData = await archiveResponse.json().catch(() => ({ error: 'Unknown error' }));
          logger.warn('⚠️ Archive Edge Function returned error (continuing with deletion):', {
            status: archiveResponse.status,
            error: errorData.error || 'Unknown error',
            note: 'Records will be deleted but may not be archived.',
          });
        } else {
          const archiveResult = await archiveResponse.json();
          if (archiveResult.success) {
            logger.log(`✅ Successfully archived ${archiveResult.archivedCount || 0} records before deletion`);
          } else {
            logger.warn('⚠️ Archive Edge Function reported failure (continuing with deletion):', {
              error: archiveResult.error,
              note: 'Records will be deleted but may not be archived.',
            });
          }
        }
      } catch (archiveException: any) {
        // Archive operation failed, but continue with deletion
        logger.warn('⚠️ Exception during archiving via Edge Function (continuing with deletion):', {
          error: archiveException.message,
          note: 'Records will be deleted but may not be archived. Check Edge Function configuration.',
        });
      }
    } else {
      logger.warn('⚠️ Archive service URL not configured, skipping archiving:', {
        hasProjectRef: !!SUPABASE_PROJECT_REF,
        hasAnonKey: !!SUPABASE_ANON_KEY,
        note: 'Records will be deleted but not archived. Configure VITE_SUPABASE_PROJECT_REF and VITE_SUPABASE_ANON_KEY to enable archiving.',
      });
    }

    // Step 2: Delete original records (same conditions as getUserQueryCount)
    // For authenticated users: delete by auth_user_id
    // For anonymous users: delete by user_id
    let deleteQuery;
    if (isAuthenticated && user) {
      deleteQuery = supabase
        .from('query_history')
        .delete()
        .eq('auth_user_id', user.id);
    } else {
      deleteQuery = supabase
        .from('query_history')
        .delete()
        .eq('user_id', userId);
    }

    const { error: deleteError } = await deleteQuery;

    if (deleteError) {
      logger.error('❌ Error clearing history:', deleteError);
      return false;
    }

    // Step 3: Verify deletion by checking count
    const countAfterDeletion = await getUserQueryCount();
    if (countAfterDeletion > 0) {
      if (import.meta.env.DEV) {
        logger.warn(`⚠️ Warning: Query count after deletion is ${countAfterDeletion}, expected 0`);
      }
    } else {
      if (import.meta.env.DEV) {
        logger.log('✅ History cleared successfully, count verified as 0');
      }
    }

    // 1.2.18: 清空成功后清除缓存
    const cacheUserId = (isAuthenticated && user) ? user.id : userId;
    if (cacheUserId) {
      clearQueryHistoryCache(cacheUserId);
      if (import.meta.env.DEV) {
        logger.log('✅ Cleared query history cache after clearing all history');
      }
    }

    return true;
  } catch (error: any) {
    logger.error('❌ Exception while clearing history:', {
      error: error.message,
      stack: error.stack,
    });
    return false;
  }
}

/**
 * Update query result count and optionally save snapshot
 */
export async function updateQueryResultCount(
  id: string,
  resultCount: number,
  resultsSnapshot?: Restaurant[] | null
): Promise<{ success: boolean; error: string | null }> {
  try {
    const { userId, isAuthenticated } = await getUserId();
    const user = await getCurrentUser();
    
    if (!userId) {
      logger.error('❌ Cannot update query: user_id is missing');
      return { success: false, error: '无法更新查询：用户ID缺失' };
    }

    if (!id) {
      logger.error('❌ Cannot update query: id is missing');
      return { success: false, error: '无法更新查询：查询ID缺失' };
    }

    logger.log('🔄 Attempting to update query:', {
      id,
      userId,
      isAuthenticated,
      resultCount,
      hasSnapshot: !!resultsSnapshot,
      snapshotCount: resultsSnapshot?.length || 0
    });

    // Check if we're adding a snapshot to a query that didn't have one before
    // This is important for search mode where the query is saved first without snapshot
    // and then updated with snapshot later
    if (resultsSnapshot && resultsSnapshot.length > 0 && !isAuthenticated) {
      // First, check if the existing query already has a snapshot
      let existingQuery;
      if (user) {
        const { data } = await supabase
          .from('query_history')
          .select('results_snapshot')
          .eq('id', id)
          .eq('auth_user_id', user.id)
          .single();
        existingQuery = data;
      } else {
        const { data } = await supabase
          .from('query_history')
          .select('results_snapshot')
          .eq('id', id)
          .eq('user_id', userId)
          .single();
        existingQuery = data;
      }

      // If the query doesn't have a snapshot yet, we're adding one
      // This counts as a new query with snapshot, so check the limit
      if (!existingQuery?.results_snapshot) {
        const limitCheck = await checkQueryLimit();
        logger.log('🔍 Checking query limit before adding snapshot:', {
          hasSnapshot: !!(resultsSnapshot && resultsSnapshot.length > 0),
          isAuthenticated,
          limitCheck,
          existingHasSnapshot: !!existingQuery?.results_snapshot
        });
        
        if (!limitCheck.allowed) {
          logger.warn('⚠️ Query limit reached for anonymous user when adding snapshot:', limitCheck);
          const errorMessage = `未注册用户最多只能保存 ${limitCheck.limit} 个查询结果。请注册以保存更多查询。`;
          logger.error('❌ Query limit error:', errorMessage);
          return {
            success: false,
            error: errorMessage,
          };
        }
      }
    }

    const updateData: any = { result_count: resultCount };

    // If results snapshot is provided, save it with expiration
    if (resultsSnapshot && resultsSnapshot.length > 0) {
      updateData.results_snapshot = resultsSnapshot;
      updateData.snapshot_expires_at = getSnapshotExpirationDate();
    }

    // For authenticated users: check by auth_user_id
    // For anonymous users: check by user_id
    let query;
    if (isAuthenticated && user) {
      query = supabase
        .from('query_history')
        .update(updateData)
        .eq('id', id)
        .eq('auth_user_id', user.id)
        .select()
        .single();
    } else {
      query = supabase
        .from('query_history')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', userId)
        .select()
        .single();
    }

    const { data, error } = await query;

    if (error) {
      if (import.meta.env.DEV) {
        console.error('❌ Error updating query result count:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          id,
          userId,
          isAuthenticated,
          updateData: {
            result_count: updateData.result_count,
            hasSnapshot: !!updateData.results_snapshot,
            snapshotCount: updateData.results_snapshot?.length || 0
          }
        });
      }

      // If error is due to missing snapshot columns, try without snapshot fields
      if (error.code === 'PGRST204' && (error.message?.includes('results_snapshot') || error.message?.includes('snapshot_expires_at'))) {
        logger.warn('⚠️ Snapshot columns not found, updating without snapshot. Please run migration.');
        // Retry without snapshot fields
        let retryQuery;
        if (isAuthenticated && user) {
          retryQuery = supabase
            .from('query_history')
            .update({ result_count: resultCount })
            .eq('id', id)
            .eq('auth_user_id', user.id);
        } else {
          retryQuery = supabase
            .from('query_history')
            .update({ result_count: resultCount })
            .eq('id', id)
            .eq('user_id', userId);
        }
        
        const { error: retryError } = await retryQuery;
        
        if (retryError) {
          logger.error('❌ Error updating query result count (retry without snapshot):', retryError);
          return { success: false, error: retryError.message || '更新查询失败' };
        }
        logger.log('✅ Query updated successfully (without snapshot)');
        return { success: true, error: null };
      }
      
      return { success: false, error: error.message || '更新查询失败' };
    }

    logger.log('✅ Query updated successfully:', {
      id: data?.id,
      result_count: data?.result_count,
      hasSnapshot: !!data?.results_snapshot,
      snapshotExpiresAt: data?.snapshot_expires_at
    });

    return { success: true, error: null };
  } catch (error: any) {
    logger.error('❌ Exception while updating query result count:', {
      error: error.message,
      stack: error.stack
    });
    return { success: false, error: error.message || '更新查询失败' };
  }
}

/**
 * Get snapshot results from a query history item if valid
 */
export function getSnapshotResults(item: QueryHistoryItem): Restaurant[] | null {
  if (isSnapshotValid(item) && item.results_snapshot) {
    return item.results_snapshot;
  }
  return null;
}

